// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useEffect, useRef } from "react";
import { X, Database, CircleCheck } from "lucide-react";
import { TYPE_CONFIG } from "./constants";

const STATUS_LABELS = {
  confirmed: "Bestätigt",
  pending: "Ausstehend",
  cancelled: "Storniert",
  canceled: "Storniert",
};

const statusLabel = (status) =>
  STATUS_LABELS[(status || "").toLowerCase()] || status || "Bestätigt";

const AvailabilityDetailPanel = ({
  isOpen,
  isClosing,
  detailDate,
  detailItems,
  onClose,
}) => {
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isClosing) {
      const t = setTimeout(() => closeBtnRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen, isClosing, detailDate]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Tab" || !isOpen || isClosing) return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isClosing]);

  if (!detailDate) return null;

  const d = new Date(detailDate);
  const label = d.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const panelClass = [
    "av-detail-panel",
    isOpen && !isClosing ? "open" : "",
    isClosing ? "closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div
        className={`av-overlay${isOpen && !isClosing ? " open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        className={panelClass}
        role="dialog"
        aria-modal="true"
        aria-label="Tagesdetails"
        aria-hidden={!isOpen || isClosing}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="av-detail-close"
          onClick={onClose}
          aria-label="Detailansicht schließen"
        >
          <X size={16} aria-hidden />
        </button>
        <h2 className="av-detail-date">{label}</h2>
        <div className="av-detail-items">
          {detailItems.map((item) => {
            const cfg = TYPE_CONFIG[item.type];
            const Icon = cfg.Icon;
            return (
              <article
                key={item.id}
                className="av-detail-item"
                style={{ borderLeftColor: cfg.border }}
                aria-label={`${cfg.label}: ${item.title}`}
              >
                <div className="av-detail-item-header">
                  <span
                    className="av-type-badge"
                    style={{
                      background: cfg.bg,
                      color: cfg.text,
                      borderColor: cfg.border,
                    }}
                  >
                    <Icon size={12} aria-hidden />
                    {cfg.label}
                  </span>
                  {item.source === "seas" && (
                    <span className="av-seas-badge av-seas-badge--detail">
                      <Database size={10} aria-hidden />
                      SEAS
                    </span>
                  )}
                </div>
                <div className="av-detail-item-title">{item.title}</div>
                <div className="av-detail-rows">
                  <div className="av-detail-row">
                    <span className="av-detail-row-label">Organisation</span>
                    <span className="av-detail-row-val">{item.org}</span>
                  </div>
                  <div className="av-detail-row">
                    <span className="av-detail-row-label">Zeit</span>
                    <span className="av-detail-row-val">{item.time}</span>
                  </div>
                  <div className="av-detail-row">
                    <span className="av-detail-row-label">Referenz</span>
                    <span className="av-detail-row-val av-detail-row-mono">
                      {item.ref}
                    </span>
                  </div>
                  <div className="av-detail-row">
                    <span className="av-detail-row-label">Quelle</span>
                    <span className="av-detail-row-val">
                      {item.source === "seas"
                        ? "SEAS-Microservice"
                        : "VHP-Buchung"}
                    </span>
                  </div>
                  <div className="av-detail-row">
                    <span className="av-detail-row-label">Status</span>
                    <span
                      className="av-detail-row-val"
                      style={{
                        color: "var(--av-delivery-text)",
                        fontWeight: 600,
                      }}
                    >
                      <CircleCheck
                        size={12}
                        aria-hidden
                        style={{ verticalAlign: "middle", marginRight: 4 }}
                      />
                      {statusLabel(item.status)}
                    </span>
                  </div>
                </div>
                {item.seasNote && (
                  <div className="av-detail-seas-note">
                    <Database size={12} aria-hidden />
                    {item.seasNote}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default AvailabilityDetailPanel;
